import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePdf, Storefront, Handshake, SealCheck, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const TIERS = [["1 – 49", "79 €"], ["50 – 99", "69 €"], ["100 – 249", "59 €"], ["250 – 999", "49 €"], ["1.000 +", "da 39 €"]];
const MODELS = [
  { k: "resale", i: Storefront, t: "Rivendita", d: "Una sola fattura mensile per tutte le strutture. Rivendi Otelya al prezzo che vuoi e tieni il margine." },
  { k: "referral", i: Handshake, t: "Segnalazione", d: "Le strutture si abbonano dal tuo link. Ricevi il 20% di commissione ricorrente, senza gestire nulla." },
  { k: "cobrand", i: SealCheck, t: "Co-branding", d: "Come la Rivendita, con il tuo nome e logo accanto a Otelya nella dashboard delle tue strutture." },
];

function Hero() {
  return (
    <section className="max-w-6xl mx-auto px-6 pt-16 pb-12">
      <div className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Programma Partner</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] max-w-4xl">Una receptionist AI per tutte le tue strutture. Un solo contratto.</h1>
      <p className="text-base md:text-lg text-stone mt-6 max-w-2xl">Per gestori, reti e portali con decine o migliaia di strutture: Console Partner unica, fattura unica e prezzi che scendono con il volume.</p>
      <div className="flex flex-wrap gap-3 mt-8">
        <Button asChild className="rounded-full bg-ink text-canvas hover:bg-olive h-12 px-6"><a href="#partner-signup" data-testid="partner-hero-cta">Diventa Partner <ArrowRight size={16} className="ml-2" /></a></Button>
        <Button asChild variant="outline" className="rounded-full h-12 px-6"><a href={`${API}/api/partner/proposal.pdf`} target="_blank" rel="noreferrer" data-testid="partner-pdf-btn"><FilePdf size={18} className="mr-2" />Scarica la proposta PDF</a></Button>
      </div>
    </section>
  );
}

function Models() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-4">
      {MODELS.map((m, i) => (
        <div key={m.k} className="rounded-2xl border border-line bg-canvas p-6 transition-transform hover:-translate-y-1" style={{ animationDelay: `${i * 80}ms` }} data-testid={`partner-model-${m.k}`}>
          <m.i size={28} className="text-olive" />
          <div className="font-serif text-2xl text-ink mt-4">{m.t}</div>
          <p className="text-sm text-stone mt-2">{m.d}</p>
        </div>
      ))}
    </section>
  );
}

function Pricing() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-8 items-start">
      <div>
        <div className="font-serif text-3xl text-ink">Prezzi a scaglioni</div>
        <p className="text-sm text-stone mt-2">Per struttura al mese, impegno 12 mesi, IVA esclusa. Listino pubblico: 199 €. Lo scaglione vale per tutte le strutture attive.</p>
        <div className="mt-5 rounded-2xl overflow-hidden border border-line" data-testid="partner-tiers">
          {TIERS.map(([n, p], i) => (
            <div key={n} className={`flex justify-between px-5 py-3 text-sm ${i === 2 ? "bg-gold/20" : i % 2 ? "bg-sand/50" : "bg-canvas"}`}><span>{n} strutture</span><b>{p}</b></div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl bg-olive text-canvas p-8" data-testid="partner-pilot">
        <div className="text-xs uppercase tracking-[0.25em] text-gold">Progetto pilota · 60 giorni</div>
        <div className="font-serif text-3xl mt-3">10 – 20 strutture a 49 € per struttura</div>
        <p className="text-sm text-canvas/80 mt-3">Fissiamo insieme gli obiettivi. Se vengono raggiunti, si passa al listino Partner del tuo scaglione. Nessun costo di attivazione.</p>
        <ul className="text-sm text-canvas/90 mt-5 space-y-1.5">
          <li>· 150 minuti voce per struttura, in un monte unico condiviso</li>
          <li>· Tariffe WhatsApp di Meta al costo</li>
          <li>· Attivazione da CSV, inviti automatici ai titolari</li>
        </ul>
      </div>
    </section>
  );
}

function SignupForm() {
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ company_name: "", full_name: "", email: "", password: "", phone: "", vat: "", model: "resale", est_properties: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/partner/signup", { ...f, est_properties: Number(f.est_properties) || 0 });
      await loginWithToken(r.data.token);
      toast.success("Richiesta inviata: la approviamo entro 24 ore");
      nav("/dashboard");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d || "Errore");
    } finally { setBusy(false); }
  };
  return (
    <section id="partner-signup" className="max-w-3xl mx-auto px-6 py-16">
      <div className="font-serif text-3xl text-ink">Richiedi l'accesso Partner</div>
      <p className="text-sm text-stone mt-2">Approviamo ogni richiesta entro 24 ore lavorative.</p>
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-3 mt-6" data-testid="partner-signup-form">
        <Input required placeholder="Ragione sociale" value={f.company_name} onChange={set("company_name")} data-testid="partner-company" />
        <Input placeholder="Partita IVA" value={f.vat} onChange={set("vat")} data-testid="partner-vat" />
        <Input required placeholder="Nome e cognome" value={f.full_name} onChange={set("full_name")} data-testid="partner-fullname" />
        <Input required placeholder="Telefono" value={f.phone} onChange={set("phone")} data-testid="partner-phone" />
        <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="partner-email" />
        <Input required type="password" placeholder="Password (min 8 caratteri)" value={f.password} onChange={set("password")} data-testid="partner-password" />
        <Select value={f.model} onValueChange={(v) => setF({ ...f, model: v })}>
          <SelectTrigger data-testid="partner-model-select"><SelectValue /></SelectTrigger>
          <SelectContent>{MODELS.map((m) => <SelectItem key={m.k} value={m.k}>{m.t}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" min="0" placeholder="Strutture nel tuo portafoglio" value={f.est_properties} onChange={set("est_properties")} data-testid="partner-est" />
        <Textarea className="md:col-span-2" rows={3} placeholder="Note (opzionale)" value={f.notes} onChange={set("notes")} data-testid="partner-notes" />
        <Button disabled={busy} className="md:col-span-2 rounded-full bg-ink text-canvas hover:bg-olive h-12" data-testid="partner-signup-submit">{busy ? "Invio…" : "Invia richiesta"}</Button>
      </form>
      <p className="text-xs text-stone mt-4">Hai già un account Partner? <Link to="/auth" className="underline">Accedi</Link></p>
    </section>
  );
}

export default function PartnerPage() {
  return (
    <div className="min-h-screen bg-canvas" data-testid="partner-page">
      <header className="max-w-6xl mx-auto px-6 py-6 flex justify-between items-center"><Link to="/"><Logo /></Link><Link to="/auth" className="text-sm text-stone hover:text-ink">Accedi</Link></header>
      <Hero /><Models /><Pricing /><SignupForm />
      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-stone">Otelya · New Farming S.r.l. Società Agricola · alfio@otelya.app · +39 349 639 5904</footer>
    </div>
  );
}
